/**
 * OAuth 回跳網址片段的收件規則（批185，審查 FE-07／LS-06）。
 *
 * 在此之前 SPA 收下**任何**網址片段裡的 `#access_token=`：攻擊者只要讓受害者點一條
 * `https://<後台>/#access_token=<攻擊者自己的 token>`，受害者的分頁就以攻擊者的身分運作
 * （登入 CSRF；之後在那個 session 裡做的事都記到攻擊者帳號下）。
 *
 * 現在只收「**本分頁剛發起的登入**」帶回來的 token：
 *
 * 1. 按〈使用 Google 登入〉時產生一次性 nonce，存 sessionStorage（只活在這個分頁），
 *    並以 `/api/<realm>/auth/google/start?nonce=…` 帶進 LS 自簽的 OAuth state。
 * 2. LS 的 callback 把 state 裡的 nonce 原樣帶回片段（`#access_token=…&nonce=…`）。
 * 3. 片段的 nonce 與 sessionStorage 的一致才收，收完即刪（一次性）；其餘一律丟棄。
 *
 * 唯一例外是 **dev-login**（只在 LS 的 dev 模式存在，正式環境是 404）：本機截圖腳本直接打
 * `/api/<realm>/dev-login`，沒有按鈕可以先存 nonce。它簽的 token 帶 `dl: true` claim——片段**沒有**
 * nonce 時，只收帶這個 claim 的 token，而且本機原本沒有 token（偽造一張也只會在第一個 API
 * 請求就 401；有 token 時不收，避免被一條連結把現有的登入換掉）。
 *
 * ★ portal 有一份同名同內容的 `portal/src/lib/loginFragment.ts`（兩個 app 各自打包、不共用原始碼）。
 */

export const LOGIN_NONCE_KEY = 'ipo_ls_login_nonce'

function base64url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** 產生一次性 nonce 並記在本分頁的 sessionStorage；回傳要導去的 start 網址。 */
export function loginStartUrl(startPath: string): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  const nonce = base64url(bytes)
  try {
    sessionStorage.setItem(LOGIN_NONCE_KEY, nonce)
  } catch {
    // 無痕模式可能存不進去：照樣導去登入，回來時 nonce 對不上＝不收，使用者再按一次即可
  }
  return `${startPath}?nonce=${encodeURIComponent(nonce)}`
}

function storedNonce(): string | null {
  try {
    return sessionStorage.getItem(LOGIN_NONCE_KEY)
  } catch {
    return null
  }
}

function clearNonce(): void {
  try {
    sessionStorage.removeItem(LOGIN_NONCE_KEY)
  } catch {
    // 讀不到也刪不掉：沒有可清的東西
  }
}

/** JWT 的 payload（**不驗章**——只用來判斷 dev-login 的 claim；權限一律由伺服器驗）。 */
export function jwtClaims(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const value: unknown = JSON.parse(json)
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
  } catch {
    return null
  }
}

export interface FragmentToken {
  /** 片段裡有這個鍵（呼叫端據此把片段從網址列清掉——不論收不收）。 */
  present: boolean
  /** 通過檢查、可以採用的 token；不收＝`null`。 */
  token: string | null
}

/**
 * 從網址片段取 `key`（`access_token`／`pending_token`），依上面的規則決定收不收。
 *
 * @param hasToken 本機目前已有登入 token（dev-login 的例外只在沒有時才適用）。
 */
export function takeFragmentToken(hash: string, key: string, opts: { hasToken: boolean }): FragmentToken {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const raw = params.get(key)
  if (!raw) return { present: false, token: null }
  const nonce = params.get('nonce') ?? ''
  if (nonce) {
    const expected = storedNonce()
    if (expected && expected === nonce) {
      clearNonce()
      return { present: true, token: raw }
    }
    return { present: true, token: null }
  }
  if (!opts.hasToken && jwtClaims(raw)?.dl === true) return { present: true, token: raw }
  return { present: true, token: null }
}
